import type { EasyModePlaceSelection } from "@/types/easyModePlace";
import { FIND_RIDE_COLORS } from "../constants/findRide.constants";
import {
  formatSearchSummaryDate,
  formatSearchSummarySeats,
  getPlaceHierarchyLabels,
} from "../utils/placeHierarchyLabels";

type EndpointMarkerProps = {
  variant: "from" | "to";
};

function EndpointMarker({ variant }: EndpointMarkerProps) {
  if (variant === "from") {
    return (
      <span
        className="mt-1.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: FIND_RIDE_COLORS.pinGreen }}
        aria-hidden
      >
        <span className="h-1.5 w-1.5 rounded-full bg-white" />
      </span>
    );
  }

  return (
    <span
      className="mt-1.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border-2 bg-white"
      style={{ borderColor: "#EF4444" }}
      aria-hidden
    >
      <span className="h-1.5 w-1.5 rounded-full bg-white" />
    </span>
  );
}

type PlaceColumnProps = {
  place: EasyModePlaceSelection;
  variant: "from" | "to";
};

function PlaceColumn({ place, variant }: PlaceColumnProps) {
  const { primary, secondary } = getPlaceHierarchyLabels(place);

  return (
    <div className="flex min-w-0 flex-1 gap-2.5 text-left">
      <EndpointMarker variant={variant} />
      <div className="min-w-0">
        <p
          className="truncate text-lg font-bold leading-tight sm:text-xl"
          style={{ color: FIND_RIDE_COLORS.textPrimary }}
        >
          {primary}
        </p>
        {secondary ? (
          <p className="mt-0.5 truncate text-sm text-gray-500">{secondary}</p>
        ) : null}
      </div>
    </div>
  );
}

export type FindRideRouteSummaryProps = {
  pickup: EasyModePlaceSelection;
  drop: EasyModePlaceSelection;
  date: string;
  seats: number;
};

/**
 * Trip summary strip: From → To with locality/city (or city/state) hierarchy.
 */
export function FindRideRouteSummary({
  pickup,
  drop,
  date,
  seats,
}: FindRideRouteSummaryProps) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white px-4 py-4 shadow-sm sm:px-6 sm:py-5">
      <div className="flex items-start gap-3 sm:gap-6">
        <PlaceColumn place={pickup} variant="from" />
        <span
          className="mt-1 shrink-0 text-xl font-light text-gray-400"
          aria-hidden
        >
          →
        </span>
        <PlaceColumn place={drop} variant="to" />
      </div>

      <p className="mt-4 text-center text-xs font-medium uppercase tracking-wide text-gray-500 sm:text-sm">
        {formatSearchSummaryDate(date)}
        <span className="mx-2 text-gray-300">|</span>
        {formatSearchSummarySeats(seats)}
      </p>
    </div>
  );
}
